import { EpimsBankDetailDto } from '@cradua/data';
import { createReducer, on, Action } from '@ngrx/store';
import { EntityState, EntityAdapter, createEntityAdapter } from '@ngrx/entity';

import * as EpimsBankDetailActions from './bank-detail.actions';

export const BANK_DETAIL_FEATURE_KEY = 'bankdetail';

export interface EpimsBankDetailState extends EntityState<EpimsBankDetailDto> {
  selectedEpimsBankDetailId?: string | number; // which EpimsBankDetail record has been selected
  loaded: boolean; // has the EpimsBankDetail list been loaded
  error?: string | null; // last none error (if any)
}

export interface EpimsBankDetailPartialState {
  readonly [BANK_DETAIL_FEATURE_KEY]: EpimsBankDetailState;
}

export const bankDetailAdapter: EntityAdapter<EpimsBankDetailDto> = createEntityAdapter<
  EpimsBankDetailDto
>({
  selectId: (bankDetail) => bankDetail.id,
  sortComparer: false,
});

export const initialState: EpimsBankDetailState = bankDetailAdapter.getInitialState(
  {
    // set initial required properties
    loaded: false,
    error: null,
    selectedEpimsBankDetailId: null,
  }
);

const bankDetailReducer = createReducer(
  initialState,
  on(EpimsBankDetailActions.loadEpimsBankDetail, (state) => ({
    ...state,
    loaded: true,
    error: null,
  })),
  on(EpimsBankDetailActions.loadEpimsBankDetailSuccess, (state, { bank }) =>
    bankDetailAdapter.addAll(bank, { ...state, loaded: false })
  ),
  on(EpimsBankDetailActions.loadEpimsBankDetailFailure, (state, { error }) => ({
    ...state,
    loaded: false,
    error,
  })),
  on(EpimsBankDetailActions.addEpimsBankDetail, (state) => ({
    ...state,
    loaded: true,
    error: null,
  })),
  on(
    EpimsBankDetailActions.addEpimsBankDetailSuccess,
    (state, { newEpimsBankDetail }) =>
      bankDetailAdapter.addMany(newEpimsBankDetail, { ...state, loaded: false })
  ),
  on(EpimsBankDetailActions.addEpimsBankDetailFailure, (state, { error }) => ({
    ...state,
    loaded: false,
    error,
  })),
  on(EpimsBankDetailActions.updateEpimsBankDetail, (state) => ({
    ...state,
    loaded: true,
    error: null,
  })),
  on(
    EpimsBankDetailActions.updateEpimsBankDetailSuccess,
    (state, { updateEpimsBankDetail }) =>
      bankDetailAdapter.upsertMany(updateEpimsBankDetail, {
        ...state,
        loaded: false,
      })
  ),
  on(
    EpimsBankDetailActions.updateEpimsBankDetailFailure,
    (state, { error }) => ({
      ...state,
      loaded: false,
      error,
    })
  ),
  on(EpimsBankDetailActions.removeEpimsBankDetail, (state) => ({
    ...state,
    loaded: true,
    error: null,
  })),
  on(EpimsBankDetailActions.removeEpimsBankDetailSuccess, (state, { ids }) =>
    bankDetailAdapter.removeMany(ids, { ...state, loaded: false })
  ),
  on(
    EpimsBankDetailActions.removeEpimsBankDetailFailure,
    (state, { error }) => ({
      ...state,
      loaded: false,
      error,
    })
  )
);

export function reducer(
  state: EpimsBankDetailState | undefined,
  action: Action
) {
  return bankDetailReducer(state, action);
}
